import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getUkTaxSettings,
  updateUkTaxSettings,
  getCisSubcontractors,
  createCisSubcontractor,
  deleteCisSubcontractor,
  getMtdStatus,
  getCt600,
  getUkTaxSummary,
  getUkSaEstimate,
  getUkDeadlines,
  getUkFilingDrafts,
} from '../api/ukTax';

export function useUkTaxSettings() {
  return useQuery({
    queryKey: ['taxes', 'settings'],
    queryFn: getUkTaxSettings,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateUkTaxSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUkTaxSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxes', 'settings'] });
      toast.success('Tax settings updated');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to update tax settings');
    },
  });
}

export function useCisSubcontractors() {
  return useQuery({
    queryKey: ['taxes', 'cis', 'subcontractors'],
    queryFn: getCisSubcontractors,
    staleTime: 2 * 60 * 1000,
  });
}

export function useMtdStatus() {
  return useQuery({
    queryKey: ['taxes', 'mtd-status'],
    queryFn: getMtdStatus,
    staleTime: 10 * 60 * 1000,
  });
}

export function useCt600Summary(taxYear?: string) {
  return useQuery({
    queryKey: ['taxes', 'ct600', taxYear],
    queryFn: () => getCt600(taxYear),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUkTaxSummary(taxYear?: string) {
  return useQuery({
    queryKey: ['taxes', 'summary', taxYear],
    queryFn: () => getUkTaxSummary(taxYear),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUkSaEstimate(taxYear?: string) {
  return useQuery({
    queryKey: ['taxes', 'sa-estimate', taxYear],
    queryFn: () => getUkSaEstimate(taxYear),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUkDeadlines() {
  return useQuery({
    queryKey: ['taxes', 'deadlines'],
    queryFn: getUkDeadlines,
    staleTime: 10 * 60 * 1000,
  });
}

export function useUkFilingDrafts() {
  return useQuery({
    queryKey: ['taxes', 'filing-drafts'],
    queryFn: getUkFilingDrafts,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCisSubcontractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCisSubcontractor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxes', 'cis', 'subcontractors'] });
      toast.success('Subcontractor added');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to add subcontractor');
    },
  });
}

export function useDeleteCisSubcontractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCisSubcontractor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxes', 'cis', 'subcontractors'] });
      toast.success('Subcontractor removed');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to remove subcontractor');
    },
  });
}
